import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { CURRENCY_OPTIONS, PRIORITY_OPTIONS, STAGE_OPTIONS } from '../constants';
import { getAssignedId } from '../types';
import type {
  CreateDealPayload,
  Deal,
  DealCurrency,
  DealPriority,
  DealStage,
  UpdateDealPayload,
  UserOption,
} from '../types';

interface DealFormModalProps {
  open: boolean;
  deal?: Deal | null; // null/undefined = create mode
  users: UserOption[];
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateDealPayload | UpdateDealPayload) => void;
}

interface FormState {
  title: string;
  stage: DealStage;
  priority: DealPriority;
  value: string;
  currency: DealCurrency;
  probability: string;
  expected_close: string;
  assigned_to: string;
  contact_name: string;
  phone: string;
  email: string;
  business_name: string;
  service_required: string;
  notes: string;
}

const EMPTY: FormState = {
  title: '',
  stage: 'prospecting',
  priority: 'medium',
  value: '',
  currency: 'INR',
  probability: '20',
  expected_close: '',
  assigned_to: '',
  contact_name: '',
  phone: '',
  email: '',
  business_name: '',
  service_required: '',
  notes: '',
};

function dealToForm(deal: Deal): FormState {
  return {
    title: deal.title ?? '',
    stage: deal.stage ?? 'prospecting',
    priority: (deal.priority as DealPriority) ?? 'medium',
    value: deal.value != null ? String(deal.value) : '',
    // Keep legacy/non-offered currencies valid in edit mode by falling back to INR
    currency: (CURRENCY_OPTIONS.some((c) => c.value === deal.currency)
      ? (deal.currency as DealCurrency)
      : 'INR'),
    probability: deal.probability != null ? String(deal.probability) : '20',
    expected_close: deal.expected_close ?? '',
    assigned_to: getAssignedId(deal.assigned_to),
    contact_name: deal.contact_name ?? '',
    phone: deal.phone ?? '',
    email: deal.email ?? '',
    business_name: deal.business_name ?? '',
    service_required: deal.service_required ?? '',
    notes: deal.notes ?? '',
  };
}

export function DealFormModal({ open, deal, users, loading, onClose, onSubmit }: DealFormModalProps) {
  const isEdit = !!deal;
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!open) return;
    setForm(deal ? dealToForm(deal) : EMPTY);
  }, [open, deal]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    const payload: CreateDealPayload = {
      title: form.title.trim(),
      stage: form.stage,
      priority: form.priority,
      value: form.value ? Number(form.value) : 0,
      currency: form.currency,
      probability: form.probability ? Number(form.probability) : 0,
      expected_close: form.expected_close || null,
      assigned_to: form.assigned_to || null,
      contact_name: form.contact_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      business_name: form.business_name.trim(),
      service_required: form.service_required.trim(),
      notes: form.notes.trim(),
    };

    onSubmit(isEdit ? (payload as UpdateDealPayload) : payload);
  };

  const assigneeOptions = [
    { value: '', label: '— Assign to me —' },
    ...users.map((u) => ({ value: u.id, label: u.display_name })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Deal' : 'New Deal'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="deal-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Create Deal'}
          </Button>
        </>
      }
    >
      <form id="deal-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Deal Title *"
          name="deal-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Deal title…"
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Stage"
            name="deal-stage"
            value={form.stage}
            onChange={(e) => set('stage', e.target.value as DealStage)}
            options={STAGE_OPTIONS}
          />
          <Select
            label="Priority"
            name="deal-priority"
            value={form.priority}
            onChange={(e) => set('priority', e.target.value as DealPriority)}
            options={PRIORITY_OPTIONS}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Value"
            name="deal-value"
            type="number"
            min={0}
            step={1000}
            value={form.value}
            onChange={(e) => set('value', e.target.value)}
            placeholder="0"
          />
          <Select
            label="Currency"
            name="deal-currency"
            value={form.currency}
            onChange={(e) => set('currency', e.target.value as DealCurrency)}
            options={CURRENCY_OPTIONS}
          />
          <Input
            label="Probability (%)"
            name="deal-probability"
            type="number"
            min={0}
            max={100}
            value={form.probability}
            onChange={(e) => set('probability', e.target.value)}
            placeholder="0"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Expected Close"
            name="deal-expected-close"
            type="date"
            value={form.expected_close}
            onChange={(e) => set('expected_close', e.target.value)}
          />
          <Select
            label="Assigned To"
            name="deal-assigned-to"
            value={form.assigned_to}
            onChange={(e) => set('assigned_to', e.target.value)}
            options={assigneeOptions}
          />
        </div>

        {/* Contact details (integration point: Contacts / Lead conversion) */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Contact Name"
            name="deal-contact-name"
            value={form.contact_name}
            onChange={(e) => set('contact_name', e.target.value)}
            placeholder="Contact person…"
          />
          <Input
            label="Business Name"
            name="deal-business-name"
            value={form.business_name}
            onChange={(e) => set('business_name', e.target.value)}
            placeholder="Company…"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Phone"
            name="deal-phone"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="Phone…"
          />
          <Input
            label="Email"
            name="deal-email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="email@example.com"
          />
        </div>

        <Input
          label="Service Required"
          name="deal-service"
          value={form.service_required}
          onChange={(e) => set('service_required', e.target.value)}
          placeholder="Service / product…"
        />

        <div>
          <label htmlFor="deal-notes" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Notes
          </label>
          <textarea
            id="deal-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Notes…"
            className="w-full resize-none rounded-DEFAULT border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
      </form>
    </Modal>
  );
}
